export const PAGE_KEYS_BY_MODULE = {
  documents: [
    "documents",
  ],
  commercial: [
    "dashboard",
    "pipeline",
    "quotes",
    "agenda",
    "workshops",
    "portfolio",
    "stats",
    "map",
  ],
  gmao: [
    "dashboard",
    "installations",
    "sav",
    "interventions",
    "planning",
    "maintenance",
    "machines",
    "parts",
  ],
  mail: [
    "mail",
  ],
  acquisition: [
    "dashboard",
    "leads",
    "campaigns",
    "connections",
    "settings",
  ],
};

export const DOCUMENTS_PAGE_KEYS =
  PAGE_KEYS_BY_MODULE.documents;

export const COMMERCIAL_PAGE_KEYS =
  PAGE_KEYS_BY_MODULE.commercial;

export const GMAO_PAGE_KEYS =
  PAGE_KEYS_BY_MODULE.gmao;

export const ACCESS_MODULE_KEYS =
  Object.keys(PAGE_KEYS_BY_MODULE);

const INSTALLATION_MANAGER_ROLES = new Set([
  "admin",
  "maintenance_manager",
]);

const NON_ADMIN_MODULE_KEYS = [
  "documents",
  "commercial",
  "gmao",
  "mail",
];

export const GRANTABLE_MODULES_BY_ROLE = {
  manager: NON_ADMIN_MODULE_KEYS,
  user: NON_ADMIN_MODULE_KEYS,
  // Le responsable maintenance peut aussi exercer une activite
  // commerciale/documentaire si l'administrateur l'autorise.
  maintenance_manager: NON_ADMIN_MODULE_KEYS,
  technician: ["gmao", "mail"],
};

export function grantableModulesForRole(role) {
  if (role === "admin") {
    return ACCESS_MODULE_KEYS;
  }

  return GRANTABLE_MODULES_BY_ROLE[role] || [];
}

const DEFAULT_MODULES_BY_ROLE = {
  admin: {
    documents: true,
    commercial: true,
    gmao: true,
    mail: true,
    acquisition: true,
  },
  manager: {
    documents: true,
    commercial: true,
    gmao: false,
    mail: true,
    acquisition: false,
  },
  maintenance_manager: {
    documents: false,
    commercial: false,
    gmao: true,
    mail: true,
    acquisition: false,
  },
  user: {
    documents: true,
    commercial: true,
    gmao: false,
    mail: true,
    acquisition: false,
  },
  technician: {
    documents: false,
    commercial: false,
    gmao: true,
    mail: true,
    acquisition: false,
  },
};

export function normalizeAccessPermissions(
  role,
  raw
) {
  const defaults =
    DEFAULT_MODULES_BY_ROLE[role] || {
      documents: false,
      commercial: false,
      gmao: false,
      mail: false,
    };

  // L'administrateur conserve toujours tous les accès.
  const isAdmin = role === "admin";

  const grantableModules =
    grantableModulesForRole(role);

  const modules = Object.fromEntries(
    ACCESS_MODULE_KEYS.map((module) => [
      module,
      isAdmin
        ? true
        : grantableModules.includes(module)
          ? raw?.modules?.[module]
            ?? defaults[module]
            ?? false
          : false,
    ])
  );

  const pages = Object.fromEntries(
    ACCESS_MODULE_KEYS.map((module) => [
      module,
      Object.fromEntries(
        PAGE_KEYS_BY_MODULE[module].map(
          (page) => {
            const defaultPageAccess =
              defaults[module] === true;

            return [
              page,
              modules[module]
                ? (
                    isAdmin
                      ? true
                      : raw?.pages?.[module]?.[page]
                        ?? defaultPageAccess
                  )
                : false,
            ];
          }
        )
      ),
    ])
  );

  if (!INSTALLATION_MANAGER_ROLES.has(role)) {
    pages.gmao.installations = false;
  }

  return {
    modules,
    pages,
  };
}

export function userRoles(user) {
  if (user?.role === "admin" || user?.role === "saas_admin") return [user.role];
  if (Array.isArray(user?.roles)) return [...new Set(user.roles)];
  return user?.actualRole || user?.role ? [user?.actualRole || user?.role] : [];
}

export function hasAnyRole(user, allowed) {
  return userRoles(user).some((role) => allowed.includes(role));
}

export function userAccessPermissions(user) {
  const roles = userRoles(user);
  const primary = user?.actualRole || user?.role;
  if (roles.length <= 1 || primary === "admin") {
    return normalizeAccessPermissions(primary, user?.accessPermissions);
  }
  const raw = user?.accessPermissions || {};
  const allowed = new Set(roles.flatMap(grantableModulesForRole));
  const modules = {};
  const pages = {};
  for (const module of ACCESS_MODULE_KEYS) {
    const defaultEnabled = roles.some((role) => DEFAULT_MODULES_BY_ROLE[role]?.[module]);
    modules[module] = allowed.has(module) && (raw.modules?.[module] ?? defaultEnabled) === true;
    pages[module] = {};
    for (const page of PAGE_KEYS_BY_MODULE[module]) {
      const canInstall = page !== "installations" || roles.some((role) => INSTALLATION_MANAGER_ROLES.has(role));
      pages[module][page] = Boolean(modules[module] && canInstall && (raw.pages?.[module]?.[page] ?? defaultEnabled));
    }
  }
  return { modules, pages };
}

export function hasModuleAccess(
  user,
  module
) {
  return userAccessPermissions(user).modules[module] === true;
}

export function hasPageAccess(
  user,
  module,
  page
) {
  const permissions = userAccessPermissions(user);

  return (
    permissions.modules[module] === true
    && permissions.pages?.[module]?.[page]
      === true
  );
}
